import classNames from 'classnames';
import { Link } from 'components/Link';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Variant = 'default' | 'small' | 'medium' | 'large';
type Color = 'dark' | 'darker' | 'light' | 'lighter';

export type ParagraphProps = {
  children?: React.ReactNode;
  /**
   * Variant to define the button style
   */
  variant?: Variant;
  /**
   * Color to define the button style
   */
  color?: Color;
  marginBottom?: number;
  mdMarginBottom?: number;
  lineNumber?: number;
  lineHeight?: number;
  isExpandable?: boolean;
};

export const Paragraph: React.FC<ParagraphProps> = ({
  children = 'This is a paragraph',
  variant = 'default',
  color = 'darker',
  marginBottom,
  mdMarginBottom,
  lineNumber = 4,
  lineHeight = 24,
  isExpandable,
}) => {
  const [isShowMore, setIsShowMore] = useState<boolean>(true);
  const [height, setHeight] = useState<number>();

  const { t } = useTranslation();
  const paragraphRef = useRef<HTMLParagraphElement>(null);

  // Needed to force hiding on re-render
  useEffect(() => {
    if (children) {
      setIsShowMore(true);
    }
  }, [children]);

  useEffect(() => {
    setHeight(paragraphRef.current?.offsetHeight);
  }, [paragraphRef]);

  const paragraphStyle = () => {
    const obj = {
      WebkitLineClamp: lineNumber,
      lineClamp: lineNumber,
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    };
    return isShowMore ? obj : {};
  };

  const numOfLines = !!height && Math.round(height / lineHeight);

  const manageLargeContent = numOfLines && numOfLines >= lineNumber;

  const variantClassName = {
    small: 'font-sans font-medium text-2xs leading-2',
    medium: 'font-sans font-regular text-xs leading-3',
    large: 'font-sans font-regular text-lg leading-6',
    default: 'font-sans font-regular leading-4',
  }[variant];

  const colorClassName = {
    lighter: 'text-white',
    light: 'text-grey-300',
    dark: 'text-grey-600',
    darker: 'text-grey-700',
  }[color];

  return isExpandable ? (
    <>
      <p
        ref={paragraphRef}
        className="text-grey-700 leading-4"
        style={paragraphStyle()}
      >
        {children}
      </p>
      {manageLargeContent && (
        <p className="mt-1">
          <Link
            label={t(`linksOrButtons:${isShowMore ? 'showMore' : 'showLess'}`)}
            onClick={() => setIsShowMore(!isShowMore)}
          />
        </p>
      )}
    </>
  ) : (
    <p
      className={classNames(
        variantClassName,
        colorClassName,
        marginBottom ? `mb-${marginBottom}` : '',
        mdMarginBottom ? `md:mb-${mdMarginBottom}` : '',
      )}
    >
      {children}
    </p>
  );
};
